// TamalePage.jsx
import React, { useState, useEffect } from "react";
import MapVisualization from "./components/MapVisualization";
import HeatmapChart from "./components/HeatmapChart";
import TimeSeriesChart from "./components/TimeSeriesChart";
import SpatialGridOverlay from "./components/SpatialGridOverlay";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css"; // Assuming the styling is shared from App.css

// Dataset files for Tamale
const ncFiles = [
  "cdd_National_CHIRPS.nc",
  "cwd_National_CHIRPS.nc",
  "ghana_climate_indices_mean.csv",
  "prcptot_National_CHIRPS.nc",
  "r10mm_National_CHIRPS.nc",
  "r20mm_National_CHIRPS.nc",
  "r30mm_National_CHIRPS.nc",
  "r95p_National_CHIRPS.nc",
  "r95ptot_National_CHIRPS.nc",
  "r99p_National_CHIRPS.nc",
  "r99ptot_National_CHIRPS.nc",
  "rx1day_National_CHIRPS.nc",
  "rx5day_National_CHIRPS.nc",
  "rx7day_National_CHIRPS.nc",
  "sdii_National_CHIRPS.nc",
];

// Descriptions for the Tamale dataset variables
const descriptions = {
  cdd: "Consecutive dry days",
  cwd: "Consecutive wet days",
  ghana_climate_indices_mean: "Ghana climate indices mean (CSV file)",
  prcptot: "Annual total precipitation",
  r10mm: "Days with ≥10mm precipitation",
  r20mm: "Days with ≥20mm precipitation",
  r30mm: "Days with ≥30mm precipitation",
  r95p: "Very wet days (95th percentile)",
  r95ptot: "Total precipitation from very wet days",
  r99p: "Extremely wet days (99th percentile)",
  r99ptot: "Total precipitation from extremely wet days",
  rx1day: "Maximum 1-day precipitation",
  rx5day: "Maximum 5-day precipitation",
  rx7day: "Maximum 7-day precipitation",
  sdii: "Simple Daily Intensity Index",
};

function TamalePage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [geojson, setBoundaryGeoJson] = useState({});
  useEffect(() => {
    const fetchBoundary = async () => {
      try {
        const geojson = {
          type: "FeatureCollection",
          name: "Ghana",
          crs: {
            type: "name",
            properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
          },
          features: [
            {
              type: "Feature",
              properties: {
                Label: "Ghana",
                Country: "Ghana",
              },
            },
          ],
        };

        setBoundaryGeoJson(geojson);
      } catch (err) {
        console.error("Failed to load Ghana boundary", err);
      }
    };

    fetchBoundary();
  }, []);
  const handleVisualize = async (file) => {
    // The API endpoint is updated for the Tamale dataset
    const url = `https://ghclimatevulnerabilityhub.com/dataset/National/${file}`;
    setSelectedFile(file);
    setLoading(true);
    setResponse(null);
    setSelectedLocation(null);

    try {
      const res = await fetch(
        `https://climate.misconm.org/api/load?url=${encodeURIComponent(url)}`
      );
      const json = await res.json();

      if (json.error) {
        alert("Error loading data: " + json.error);
        return;
      }

      const validVariables = json.variables.filter(
        (v) => !["time", "latitude", "longitude", "spatial_ref"].includes(v)
      );

      if (validVariables.length === 0) {
        alert("No valid visualizable variable found in this dataset.");
        return;
      }

      setResponse(json);
      setSelectedVariable(validVariables[0]);
    } catch (err) {
      console.error("Failed to load file", err);
      alert("Failed to load file.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tamale-page-container">
      <header className="page-header">
        <h1 className="page-title">National Dataset Viewer</h1>
      </header>

      <main className="page-content">
        <section className="file-list-section">
          <h2 className="section-title">📁 Available Datasets</h2>
          <div className="file-list-grid">
            {ncFiles.map((file) => {
              const key = file.split("_")[0];
              const isSelected = selectedFile === file;
              return (
                <div
                  key={file}
                  className={`file-card ${isSelected ? "selected" : ""}`}
                >
                  <div className="file-info">
                    <p className="file-name">{file}</p>
                    <p className="file-description">
                      {descriptions[key] || "No description"}
                    </p>
                  </div>
                  <button
                    className="visualize-button"
                    onClick={() => handleVisualize(file)}
                    disabled={loading && isSelected}
                  >
                    {loading && isSelected ? "Loading..." : "Visualize"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {loading && (
          <div className="loading-message-container">
            <div className="spinner"></div>
            <p>Loading dataset...</p>
          </div>
        )}

        {response && selectedVariable && (
          <section className="visualization-section">
            <div className="visualization-grid">
              <div className="chart-container">
                <h3 className="chart-title">🗺 Map Visualization</h3>
                <MapVisualization
                  response={response}
                  selectedVariable={selectedVariable}
                  onLocationSelect={(loc) => setSelectedLocation(loc)}
                  boundaryGeoJson={geojson}
                />
              </div>

              <div className="chart-container">
                <h3 className="chart-title">🟦 Spatial Grid Preview</h3>
                <div style={{ padding: "20px", width: "100%", height: "100%" }}>
                  <SpatialGridOverlay
                    response={response}
                    selectedVariable={selectedVariable}
                  />
                </div>
              </div>
            </div>

            {selectedLocation && response.coords.time?.length > 0 && (
              <div className="chart-container time-series-container">
                <h3 className="chart-title">
                  📈 Time Series at ({selectedLocation.lat.toFixed(2)}°N,{" "}
                  {selectedLocation.lon.toFixed(2)}°E)
                </h3>
                <TimeSeriesChart
                  data={{
                    variables: {
                      [selectedVariable]: {
                        data: response.data[selectedVariable],
                      },
                      latitude: { data: response.coords.latitude },
                      longitude: { data: response.coords.longitude },
                      time: { data: response.coords.time },
                    },
                  }}
                  location={selectedLocation}
                  variable={selectedVariable}
                />
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default TamalePage;
